import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryDataSource } from '../../api/query';
import { raporBul, sourceIdBul } from '../../utils/raporSonek';
import { SectionCard, fmtTRY } from '../yeni-design/primitives';
import type { ReportConfig } from '../../types/panelConfig';
import type { Donem } from './donem';
import { lokasyonDestekli, lokasyonParam } from './subeSuzgeci';
import { KapsamNotu } from './KapsamNotu';
import { BlokIskeleti } from './BlokIskeleti';

/**
 * Seviye yuzdesi — `%48,1` (Turkce'de isaret ONDE). `fmtPct` KULLANILMAZ: o bir
 * DEGISIM bicimidir ve pozitif sayiya `+` ekler ("+48,1%"); birliktelik orani bir
 * degisim degil bir SEVIYE, artiyla gostermek onu "gecen doneme gore artti" diye
 * okuturdu. Ev usulu isaret-onde bicim icin bkz. BranchControlWidget.
 */
function yuzde(n: number): string {
  return '%' + n.toFixed(1).replace('.', ',');
}

interface UrunKar {
  ciro: number;
  maliyet: number;
  kar: number;
}
interface ZararSatir {
  gercek_zarar: number;
}

interface Props {
  reports: ReportConfig[];
  donem: Donem;
  sube?: string | null;
}

export interface Kopru {
  ciro: number;
  maliyet: number;
  kar: number;
  marj: number | null;
}

/**
 * Kopru RAPORUN KENDI `kar` kolonundan turer, `ciro - maliyet` HESABINDAN DEGIL.
 * Ikisi ayni rapordan geliyor ve rapor kar'i `tutar` (KDV HARIC) uzerinden,
 * ciro'yu ise `dahil_tutar` (KDV DAHIL) uzerinden hesapliyor — yani
 * `ciro - maliyet` KDV kadar sisirilmis bir "kar" uretirdi ve ekranda raporun
 * kendi kar rakamiyla CELISIRDI. Bu yuzden `maliyet` cubugu ciro'nun degil
 * kendi olcusunun payi olarak gosterilir ve fark ACIKCA yazilir.
 */
export function kopruHesapla(satirlar: UrunKar[]): Kopru {
  let ciro = 0, maliyet = 0, kar = 0;
  for (const s of satirlar) {
    ciro += Number(s.ciro ?? 0);
    maliyet += Number(s.maliyet ?? 0);
    kar += Number(s.kar ?? 0);
  }
  return { ciro, maliyet, kar, marj: ciro > 0 ? (kar / ciro) * 100 : null };
}

export function KarKoprusuBlok({ reports, donem, sube = null }: Props) {
  const rKar = raporBul(reports, 16, 'Ürün Kârlılığı');
  const idKar = sourceIdBul(reports, 16, 'Ürün Kârlılığı');
  const rZarar = raporBul(reports, 41, 'Zararına Satışlar (maliyet doğrulamalı)');
  const idZarar = sourceIdBul(reports, 41, 'Zararına Satışlar (maliyet doğrulamalı)');
  const karDestekli = lokasyonDestekli(rKar);
  const zararDestekli = lokasyonDestekli(rZarar);

  const params = (destekli: boolean) => ({
    '@BASTARIH': donem.bas,
    '@BITTARIH': donem.bit,
    ...lokasyonParam(sube, destekli),
  });

  const karQ = useQuery({
    queryKey: ['sa-kopru-kar', idKar, donem, sube, karDestekli],
    queryFn: () => queryDataSource<UrunKar>({ sourceId: idKar!, parameters: params(karDestekli) }),
    enabled: !!idKar,
    throwOnError: false,
  });

  // Zarar dilimi ISTEGE BAGLI: 041 yalnizca iki kiracida var (canlida olculdu:
  // firma 7 ve 17). Yoksa kopru yine cizilir, o satir hic basilmaz — blogun
  // tamami bir yan kaynaga bagli olmamali.
  const zararQ = useQuery({
    queryKey: ['sa-kopru-zarar', idZarar, donem, sube, zararDestekli],
    queryFn: () => queryDataSource<ZararSatir>({ sourceId: idZarar!, parameters: params(zararDestekli) }),
    enabled: !!idZarar,
    throwOnError: false,
  });

  const kopru = useMemo(() => kopruHesapla(karQ.data?.data ?? []), [karQ.data]);
  const zarar = useMemo(
    () => (zararQ.data?.data ?? []).reduce((t, r) => t + Math.abs(Number(r.gercek_zarar ?? 0)), 0),
    [zararQ.data],
  );

  if (!idKar) return null; // ana kaynak yok -> blok gizlenir
  if (karQ.isLoading)
    return (
      <BlokIskeleti
        testid="blok-kopru-iskelet"
        baslik="Kâr köprüsü"
        altBaslik="Ciro → maliyet → brüt kâr"
        yukseklik={220}
        satir={4}
      />
    );
  if (kopru.ciro === 0 && kopru.kar === 0) return null; // bos durum kurali

  const taban = Math.max(kopru.ciro, kopru.maliyet, Math.abs(kopru.kar), 1);
  const pay = (v: number) => Math.max(0, Math.min(100, (Math.abs(v) / taban) * 100));

  const cubuklar: Array<{ etiket: string; deger: number; renk: string; testid: string }> = [
    { etiket: 'Ciro', deger: kopru.ciro, renk: 'var(--color-primary)', testid: 'kopru-ciro' },
    { etiket: 'Maliyet', deger: kopru.maliyet, renk: 'var(--color-warning)', testid: 'kopru-maliyet' },
    {
      etiket: 'Brüt kâr',
      deger: kopru.kar,
      renk: kopru.kar >= 0 ? 'var(--color-positive)' : 'var(--color-negative)',
      testid: 'kopru-kar',
    },
  ];

  return (
    <div data-testid="blok-kar-koprusu">
      <SectionCard
        title="Kâr köprüsü"
        subtitle={
          <>
            Ciro → maliyet → brüt kâr
            <KapsamNotu sube={sube} destekli={karDestekli} />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {cubuklar.map((c) => (
            <div key={c.etiket} data-testid={c.testid} className="flex flex-col gap-1">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[12.5px] text-ink-600">{c.etiket}</span>
                <span className="text-[13px] tnum font-medium text-ink-900">{fmtTRY(c.deger)}</span>
              </div>
              <div className="h-2 rounded-full bg-border-light overflow-hidden">
                <div
                  data-testid={`${c.testid}-fill`}
                  style={{ width: `${pay(c.deger)}%`, background: c.renk, height: '100%' }}
                />
              </div>
            </div>
          ))}

          <div className="flex items-baseline justify-between gap-3 pt-1 border-t border-border">
            <span className="text-[12.5px] text-ink-600">Brüt kâr marjı</span>
            <span className="text-[13px] tnum font-medium text-ink-900" data-testid="kopru-marj">
              {kopru.marj === null ? '—' : yuzde(kopru.marj)}
            </span>
          </div>

          {/* 🔴 Zarar AYRI bir olcudur, kopruden CIKARILMAZ: 041 zaten 016'nin
              icindeki zararli satirlari kapsar; ayrica dusmek CIFT SAYARDI.
              Burada "brut karin ne kadari zararina satisla eriyor" diye
              baglamsal bir satir olarak duruyor. */}
          {idZarar && zarar > 0 && (
            <div className="flex items-baseline justify-between gap-3" data-testid="kopru-zarar">
              <span className="text-[12px] text-ink-500">
                Bunun içinde zararına satış
                <span className="text-[11px] text-ink-400"> · maliyet doğrulamalı</span>
              </span>
              <span className="text-[12.5px] tnum text-neg-700">−{fmtTRY(zarar)}</span>
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
